// Central API Proxy Handler - PCI Compliant Lambda Proxy
import axios from 'axios';
import sessionService from './sessionService';
import securityService from './securityService';
import { getApiName } from '../config/api';

class ApiProxy {
  constructor() {
    this.lambdaURL = 'https://api-frankfurt.rezcomm.com/central/auth/prx';
    this.timeout = 30000;
  }

  // Main proxy method with PCI compliance
  async call(apiName, data = {}, options = {}) {
    try {
      // Get authentication token
      const token = sessionService.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Validate session integrity
      if (!sessionService.isAuthenticated()) {
        throw new Error('Session integrity check failed');
      }

      // Map generic API name to correct storeurl.mjs entry based on brand
      const brandSlug = data.brand_id || sessionStorage.getItem('brandSlug');
      const mappedApiName = getApiName(apiName, brandSlug);

      // Prepare secure request payload (no UUID on frontend)
      const payload = {
        api: mappedApiName,
        data,
        token,
        timestamp: Date.now(),
        ...options
      };

      // Prepare headers with domain information for brand detection
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      // Domain headers automatically set by browser

      // Make request to Lambda central proxy
      const response = await axios.post(this.lambdaURL, payload, {
        headers,
        timeout: this.timeout
      });

      // Handle token refresh from central proxy
      if (response.data.refreshedToken) {
        const secureCookieService = await import('./secureCookieService').then(m => m.default);
        secureCookieService.setSessionToken(response.data.refreshedToken);
        delete response.data.refreshedToken;
      }

      return response.data;
    } catch (error) {
      console.error(`API Proxy call failed for ${apiName}:`, error);
      
      // Handle authentication errors with retry
      if (error.response?.status === 401 || error.message.includes('Authentication failed')) {
        try {
          await sessionService.refreshToken();
          return this.call(apiName, data, options); // Retry once
        } catch (refreshError) {
          sessionService.clearToken();
          throw new Error('Session expired. Please login again.');
        }
      }
      
      throw error;
    }
  }

  // Convenience methods for common HTTP methods
  async get(apiName, params = {}) {
    return this.call(apiName, params, { method: 'GET' });
  }

  async post(apiName, data = {}) {
    return this.call(apiName, data, { method: 'POST' });
  }

  async put(apiName, data = {}) {
    return this.call(apiName, data, { method: 'PUT' });
  }

  async delete(apiName, data = {}) {
    return this.call(apiName, data, { method: 'DELETE' });
  }

  // Brand-aware API calls
  async callWithBrand(apiName, brandCode, data = {}, options = {}) {
    const brandConfig = securityService.getBrandConfig(brandCode);
    if (!brandConfig) {
      throw new Error(`Invalid brand code: ${brandCode}`);
    }
    
    return this.call(apiName, { ...data, brand: brandCode }, options);
  }

  // Pre-authentication API calls (for brand detection)
  async callPreAuth(apiName, data = {}) {
    try {
      // Create temporary token for pre-auth APIs
      const tempToken = sessionService.createTempToken();
      
      // Map generic API name to correct storeurl.mjs entry based on brand
      const brandSlug = data.brandslug || data.brand_id;
      const mappedApiName = getApiName(apiName, brandSlug);
      
      // Prepare secure request payload
      const payload = {
        api: mappedApiName,
        data,
        token: tempToken,
        timestamp: Date.now(),
        preAuth: true
      };

      // Prepare headers with domain information
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Domain headers automatically set by browser

      // Make request to Lambda central proxy
      const response = await axios.post(this.lambdaURL, payload, {
        headers,
        timeout: this.timeout
      });

      return response.data;
    } catch (error) {
      console.error(`Pre-auth API call failed for ${apiName}:`, error);
      throw error;
    }
  }

  // Legacy compatibility method
  async callApi(apiName, requestData = {}, credentials = null) {
    return this.call(apiName, requestData);
  }
}

export default new ApiProxy();